import type { ClientSearchSortOrder } from "../store/schema/SearchTableQuery";
import { createSenderNameCollator } from "./sortRowsBySender";

/**
 * Finds the row to jump to in rows already sorted by sender (see sortRowsBySender).
 *
 * Lands on the first sender that starts with the search text, so 'mar' finds the first "Mar..."
 * sender in either direction. When no sender starts with it, A to Z lands on the first sender after
 * the text and Z to A on the first sender before it. Rows without a sender are skipped.
 *
 * @returns the index of the row to jump to; the last row with a sender when no sender matches,
 * or -1 when there are no named senders or the search text is blank
 */
export default function findSenderJumpToIndex<T>(rows: readonly T[], getSenderName: (row: T) => string | undefined, sortOrder: ClientSearchSortOrder, searchText: string): number {
    const target = searchText.trim();
    if (rows.length === 0 || !target) {
        return -1;
    }
    
    const collator = createSenderNameCollator();
    const isDescending = sortOrder === "SenderDescending";
    const senderNames = rows.map(row => getSenderName(row)?.trim());
    
    const prefixMatchIndex = senderNames.findIndex(senderName => !!senderName && collator.compare(senderName.slice(0, target.length), target) === 0);
    if (prefixMatchIndex >= 0) {
        return prefixMatchIndex;
    }
    
    // Compare full names so the numeric collation orders 'Team 9' before 'Team 10'.
    const passedTargetIndex = senderNames.findIndex(senderName => {
        if (!senderName) {
            return false;
        }
        const comparison = collator.compare(senderName, target);
        return isDescending ? comparison <= 0 : comparison >= 0;
    });
    
    if (passedTargetIndex >= 0) {
        return passedTargetIndex;
    }
    
    for (let index = senderNames.length - 1; index >= 0; index--) {
        if (senderNames[index]) {
            return index;
        }
    }
    
    return -1;
}
